import { trapiClient } from './trapiClient';
import type { Environment } from '../../contexts/AppContext';
import type { ResourcePoolObject } from '../../types/trapi';

export interface ResourcePoolStats {
  totalPools: number;
  totalBackends: number;
  averageBackendsPerPool: number;
  poolsWithoutBackends: number;
  largestPool: { name: string; backendCount: number };
  byOwner: Record<string, number>;
}

export class ResourcePoolsService {
  private static readonly BASE_PATH = '/configs';

  static async getResourcePools(params: { environment: Environment; accessToken: string; filter?: string }): Promise<ResourcePoolObject[]> {
    const { environment, accessToken, filter } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('type', 'ADMINCI');
    queryParams.append('ci', 'RESOURCEPOOL');
    if (filter && filter.includes('=')) queryParams.append('filter', filter);
    const url = `${this.BASE_PATH}?${queryParams.toString()}`;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    const response = await trapiClient.get<ResourcePoolObject[]>(url, accessToken);
    return Array.isArray(response) ? response : [];
  }

  static async getResourcePool(params: { id: string; environment: Environment; accessToken: string }): Promise<ResourcePoolObject> {
    const { id, environment, accessToken } = params;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    const url = `${this.BASE_PATH}/${encodeURIComponent(id)}`;
    return trapiClient.get<ResourcePoolObject>(url, accessToken);
  }

  static async createResourcePool(params: { environment: Environment; accessToken: string; data: Omit<ResourcePoolObject, 'id' | 'metadata'> }): Promise<ResourcePoolObject> {
    const { environment, accessToken, data } = params;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    const body = { ...data, ci: 'RESOURCEPOOL' };
    return trapiClient.put<ResourcePoolObject>(this.BASE_PATH, body, accessToken);
  }

  static async updateResourcePool(params: { environment: Environment; accessToken: string; id: string; data: Partial<Omit<ResourcePoolObject, 'id' | 'metadata'>> }): Promise<ResourcePoolObject> {
    const { environment, accessToken, id, data } = params;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    const url = `${this.BASE_PATH}/${encodeURIComponent(id)}`;
    return trapiClient.put<ResourcePoolObject>(url, data, accessToken);
  }

  static async deleteResourcePool(params: { environment: Environment; accessToken: string; id: string }): Promise<void> {
    const { environment, accessToken, id } = params;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    const url = `${this.BASE_PATH}/${encodeURIComponent(id)}`;
    await trapiClient.delete<void>(url, accessToken);
  }

  static async getResourcePoolStats(environment: Environment, accessToken: string): Promise<ResourcePoolStats> {
    const pools = await this.getResourcePools({ environment, accessToken });
    const totalBackends = pools.reduce((sum, p) => sum + (p.backends?.length || 0), 0);
    const poolsWithoutBackends = pools.filter(p => (p.backends?.length || 0) === 0).length;
    let largestPool = { name: '', backendCount: 0 };
    pools.forEach(p => {
      const c = p.backends?.length || 0;
      if (c > largestPool.backendCount) largestPool = { name: p.name, backendCount: c };
    });
    const byOwner = pools.reduce((acc: Record<string, number>, p: any) => {
      const owner = p.ownership?.owner || 'Unknown';
      acc[owner] = (acc[owner] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalPools: pools.length,
      totalBackends,
      averageBackendsPerPool: pools.length > 0 ? Math.round((totalBackends / pools.length) * 10) / 10 : 0,
      poolsWithoutBackends,
      largestPool,
      byOwner,
    };
  }
}
